import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, CheckCircle2, Lock, Loader } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ProgramDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isPremium } = useAuth();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [responses, setResponses] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get(`/programs/${id}`);
      setProgram(data);
      setResponses(data.day_responses || {});
    } catch (e) { toast.error("Could not load program"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const enroll = async () => {
    setBusy(true);
    try { await api.post(`/programs/${id}/enroll`); toast.success("Enrolled — let's begin"); await load(); }
    catch (e) { toast.error(errMsg(e, "Could not enroll")); }
    finally { setBusy(false); }
  };

  const complete = async (day) => {
    setBusy(true);
    try {
      await api.post(`/programs/${id}/complete-day`, { day, response: responses[day] || "" });
      toast.success(`Day ${day} complete`);
      await load();
    } catch (e) { toast.error("Could not complete day"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  if (!program) return <div className="px-6 py-16 text-center text-muted-foreground">Program not found.</div>;

  const days = program.days || [];
  const currentDay = program.current_day || 0;

  return (
    <div className="px-4 sm:px-6 py-8">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate("/programs")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"><ArrowLeft className="w-4 h-4" /> All programs</button>
        <span className="inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-full bg-primary/15 text-primary font-medium capitalize">{program.category}</span>
        <h1 className="font-heading text-3xl font-semibold mt-3">{program.title}</h1>
        <p className="text-muted-foreground mt-2">{program.description}</p>

        {!program.enrolled ? (
          <button data-testid="enroll-button" onClick={enroll} disabled={busy}
            className="mt-6 flex items-center gap-2 bg-primary text-primary-foreground font-semibold px-6 py-3 rounded-full hover:bg-atlantic active:scale-95 transition-all disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Enroll — {program.duration_days} days
          </button>
        ) : program.completed ? (
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-seafoam/15 text-seafoam font-medium"><CheckCircle2 className="w-5 h-5" /> Program complete 🎉</div>
        ) : (
          <div className="mt-4 text-sm text-muted-foreground">You're on day {currentDay} of {program.duration_days}.</div>
        )}

        <div className="mt-8 space-y-4" data-testid="program-days">
          {days.map((d) => {
            const locked = program.enrolled && d.day > currentDay;
            const done = program.enrolled && d.day < currentDay;
            const active = program.enrolled && d.day === currentDay;
            return (
              <div key={d.day} data-testid={`program-day-${d.day}`}
                className={`bg-card border rounded-2xl p-5 ${active ? "border-primary" : "border-border"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${done ? "bg-seafoam/20 text-seafoam" : active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
                      {done ? <CheckCircle2 className="w-4 h-4" /> : d.day}
                    </span>
                    <p className="font-semibold">{d.title}</p>
                  </div>
                  {locked && <Lock className="w-4 h-4 text-muted-foreground" />}
                </div>
                {!locked && (
                  <>
                    <p className="text-sm text-foreground/80 mt-3 leading-relaxed">{d.task || d.content}</p>
                    {program.enrolled && (active || done) && (
                      <div className="mt-3">
                        <textarea data-testid={`day-response-${d.day}`} rows={2} value={responses[d.day] || ""} disabled={!isPremium && false}
                          onChange={(e) => setResponses((r) => ({ ...r, [d.day]: e.target.value }))}
                          placeholder="Write your response…"
                          className="w-full px-3 py-2 text-sm rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none" />
                        {active && (
                          <button data-testid={`complete-day-${d.day}`} onClick={() => complete(d.day)} disabled={busy}
                            className="mt-2 flex items-center gap-2 bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 rounded-full hover:bg-atlantic active:scale-95 transition-all disabled:opacity-60">
                            {busy ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Mark day complete
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProgramDetail;
